// ManagerService — the MANAGER persona (מנהל), a company user with delegated authority that sits
// above a trainer and up to (never above) a company admin.
//
// Goes through the OpenAPI-generated client like every other service. Returns `{data}` and re-throws
// an axios-shaped error, matching the convention the other *Service modules follow so call sites read
// `res.data` and the usual error errorCode field unchanged.
//
// Every one of these endpoints is ADMIN-only server-side: a manager can never create a peer or widen
// their own grant.

import {
  adminManagerControllerList,
  adminManagerControllerGet,
  adminManagerControllerCreate,
  adminManagerControllerUpdateCapabilities,
  adminManagerControllerUpdateTrainers,
  adminManagerControllerRevoke,
  adminManagerControllerCheckAvailability,
} from '../api/generated';
import type {AdminManager, CreateManagerRequest} from '../api/generated';

/** Preserve the legacy axios rejection shape so callers reading the error's response.data.errorCode keep working. */
const throwLikeAxios = (error: unknown, response?: Response): never => {
  // eslint-disable-next-line no-throw-literal
  throw {response: {data: error, status: response?.status}};
};

export const getManagers = async (): Promise<{data: Array<AdminManager> | undefined}> => {
  const {data, error, response} = await adminManagerControllerList();
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const getManager = async (id: number) => {
  const {data, error, response} = await adminManagerControllerGet({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

/**
 * Create a manager. Pass `userId` to promote an existing company user, or the profile fields to
 * create a new one — the server takes one path or the other and never both. The company is always
 * derived from the caller's principal, never sent.
 */
export const createManager = async (request: CreateManagerRequest) => {
  const {data, error, response} = await adminManagerControllerCreate({body: request});
  if (error) throwLikeAxios(error, response);
  return {data};
};

/** Replace-set semantics: the manager ends up holding exactly `capabilities`. */
export const updateManagerCapabilities = async (
  id: number,
  capabilities: Array<'MANAGE_TRAINERS' | 'MANAGE_EXERCISES' | 'MANAGE_TEMPLATES' | 'MANAGE_GUIDES' | 'MANAGE_RECIPES' | 'MANAGE_SUPPLEMENTS' | 'MANAGE_MEASUREMENTS' | 'MANAGE_FORMS' | 'MANAGE_MENU_TEMPLATES' | 'MANAGE_FOOD' | 'MANAGE_NOTIFICATIONS' | 'MANAGE_AUTOMATIONS'>,
) => {
  const {data, error, response} = await adminManagerControllerUpdateCapabilities({
    path: {id},
    body: {capabilities},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

/**
 * Replace the trainer scope. When `managesAllTrainers` is true the id list is ignored server-side;
 * when false the list is the exact scope — an empty list means "no trainers", never "all".
 */
export const updateManagerTrainers = async (
  id: number,
  managesAllTrainers: boolean,
  trainerIds: Array<number>,
) => {
  const {data, error, response} = await adminManagerControllerUpdateTrainers({
    path: {id},
    body: {managesAllTrainers, trainerIds},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
};

/** Revoke the delegation. The underlying user is kept — they may still be a legitimate company user. */
export const revokeManager = async (id: number) => {
  const {error, response} = await adminManagerControllerRevoke({path: {id}});
  if (error) throwLikeAxios(error, response);
};

/**
 * Pre-flight duplicate check backing the create dialog's on-blur validation, so a taken email/phone
 * is reported as the user leaves the field rather than as a red toast after submit.
 */
export const checkManagerAvailability = async (args: {
  email?: string | null;
  phoneNumber?: string | null;
  excludeUserId?: number | null;
}) => {
  const {data, error, response} = await adminManagerControllerCheckAvailability({
    query: {
      email: args.email ?? undefined,
      phoneNumber: args.phoneNumber ?? undefined,
      excludeUserId: args.excludeUserId ?? undefined,
    },
  });
  if (error) throwLikeAxios(error, response);
  return {
    emailExists: !!data?.emailExists,
    phoneExists: !!data?.phoneExists,
    nameExists: !!data?.nameExists,
  };
};
